import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Button, Grid, Link, Collapse } from "@mui/material";
import { NavLink, useLocation } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ExpandLess } from "@mui/icons-material";
import clsx from "clsx";

import MegaMenuItemPresenter from "presenters/MegaMenuItemPresenter";

import useStyles from "./useStyles";

const MenuItem = ({ menuItem, closeMenu, onClick, mainMenuLinks }) => {
  const classes = useStyles();
  const [collapseSubMenu, setCollapseSubMenu] = useState(false);
  const location = useLocation();

  const name = MegaMenuItemPresenter.name(menuItem);
  const subMenuItmes = MegaMenuItemPresenter.childrenMenu(menuItem);

  const hasActiveSubLink = useMemo(() => {
    if (!subMenuItmes || subMenuItmes.length === 0) return false;

    const currentPath = location.pathname;
    const matchingSubLink = subMenuItmes.find((subMenu) => MegaMenuItemPresenter.linkTo(subMenu) === currentPath);

    if (!matchingSubLink) return false;

    return !mainMenuLinks.includes(MegaMenuItemPresenter.linkTo(matchingSubLink));
  }, [subMenuItmes, location.pathname, mainMenuLinks]);

  const handleClick = useCallback(() => {
    if (onClick) onClick();
    if (closeMenu) closeMenu();
  }, [onClick, closeMenu]);

  const handleToggleSubMenu = useCallback(() => {
    setCollapseSubMenu(!collapseSubMenu);
  }, [collapseSubMenu]);

  const renderSimpleLink = () => {
    return MegaMenuItemPresenter.isExternalLinkTo(menuItem) ? (
      <Link
        id={MegaMenuItemPresenter.createComponentId(menuItem)}
        href={MegaMenuItemPresenter.linkTo(menuItem)}
        className={classes.menuItem}
        onClick={handleClick}
      >
        {name}
      </Link>
    ) : (
      <Link
        id={MegaMenuItemPresenter.createComponentId(menuItem)}
        exact
        activeClassName={classes.activelink}
        className={classes.menuItem}
        to={MegaMenuItemPresenter.linkTo(menuItem)}
        component={NavLink}
        onClick={handleClick}
      >
        {name}
      </Link>
    );
  };

  const renderCollapsableLink = () => {
    return (
      <>
        <Grid
          className={clsx(classes.menuItem, { [classes.activelink]: hasActiveSubLink })}
          onClick={handleToggleSubMenu}
        >
          <Button
            id={`button-${MegaMenuItemPresenter.createComponentId(menuItem)}`}
            variant="link"
            endIcon={collapseSubMenu ? <ExpandLess /> : <ExpandMoreIcon />}
          >
            {name}
          </Button>
        </Grid>

        <Collapse
          hidden={!collapseSubMenu}
          in={collapseSubMenu}
          className={classes.subMenu}
          onClick={handleToggleSubMenu}
        >
          <Grid className={classes.linkList}>
            {subMenuItmes.map((subMenu) => (
              <Link
                id={MegaMenuItemPresenter.createComponentId(subMenu)}
                className={classes.linkInList}
                key={MegaMenuItemPresenter.name(subMenu)}
                to={MegaMenuItemPresenter.linkTo(subMenu)}
                component={NavLink}
                onClick={handleClick}
              >
                {subMenu.linkText}
              </Link>
            ))}
          </Grid>
        </Collapse>
      </>
    );
  };

  return subMenuItmes && subMenuItmes.length === 0 ? renderSimpleLink() : renderCollapsableLink();
};

MenuItem.propTypes = {
  menuItem: MegaMenuItemPresenter.shape(),
  closeMenu: PropTypes.func,
  onClick: PropTypes.func,
  mainMenuLinks: PropTypes.arrayOf(PropTypes.string),
};

export default MenuItem;
